import {Article} from '@/model/Article';
import {Firestore} from '@/plugins/Firestore';
import {Functions} from '@/plugins/Functions';
import {clientEnhanceRequest} from '@/service/clientEnhance';
import {OpenAiCompletionType} from '@/model/OpenAI';
import {rowsOfPartRun} from '@/service/completionRun';
import {
    GenerationStatusPatch,
    PartSnap,
    PartsTerminalDecision,
    generationAlreadyRunning,
    generationStatusPatch,
    partsRunMaySettle,
    resolvePartsTerminal,
} from '@/service/partsTerminalState';

const COMPLETION_PARTS: OpenAiCompletionType[] = ['meta', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'question'];

async function loadPartSnapshots(articleId: string): Promise<PartSnap[]> {
    const snaps: PartSnap[] = [];
    for (const part of COMPLETION_PARTS) {
        const parent = await Firestore.getArticleOpenAIState(articleId, part);
        if (!parent) {
            continue;
        }
        const values = await Firestore.getArticleOpenAIValues(articleId, part);
        snaps.push({
            part,
            count: parent.count || 0,
            parentState: parent.state,
            values: rowsOfPartRun(values, parent.runId).map((value) => ({state: value.state || null})),
        });
    }
    return snaps;
}

/**
 * Terminal status for an in-flight or stuck parts generation.
 * Opens that are not pending or processing return before any Firestore read or write.
 * Already-failed or already-done pending returns before any read.
 * The write is only `enhanced` and `aiGenerateStatus`.
 */
export async function settleProcessingParts(article: Article | null | undefined): Promise<PartsTerminalDecision> {
    if (!article?.id || !partsRunMaySettle(article)) {
        return {action: 'none'};
    }
    const parts = await loadPartSnapshots(article.id);
    const decision = resolvePartsTerminal({
        enhanced: article.enhanced,
        aiGenerateStatus: article.aiGenerateStatus,
        aiGenerateMode: article.aiGenerateMode,
        now: Date.now(),
        startedAt: article.ai_content_last_generated_at,
        parts,
    });
    const patch = generationStatusPatch(decision);
    // Success stays in memory until the article document contains the completion
    // text. Writing done here used to hide the banner and let a short save land.
    if (patch && decision.action === 'failed') {
        await Firestore.updateArticleGenerationStatus(article, patch);
    } else if (decision.action === 'wait' && decision.armClock) {
        await Firestore.updateArticleAIGeneratedAt(article);
    }
    return decision;
}

/**
 * Land running and a start timestamp before enqueue.
 * A late running write used to overwrite the terminal status, and a leftover
 * failed/timeout flag used to swallow the next run.
 */
export async function markPartsRunStarted(article: Article): Promise<void> {
    const patch: GenerationStatusPatch = {enhanced: 'processing', aiGenerateStatus: 'running'};
    await Firestore.updateArticleGenerationStatus(article, patch);
    await Firestore.updateArticleAIGeneratedAt(article);
}

/**
 * After a 409 the run already in progress is the one to follow. Its start
 * time and status come from the article (the server put the start time
 * back), so watchForAi does not drop its snapshots as older than this tab's
 * own start.
 */
export async function adoptRunInProgress(article: Article): Promise<void> {
    const latest = article.id ? await Firestore.getUserArticle(article.id) : null;
    if (!latest) {
        return;
    }
    article.ai_content_last_generated_at = latest.ai_content_last_generated_at;
    article.enhanced = latest.enhanced;
    article.aiGenerateStatus = latest.aiGenerateStatus;
}

/**
 * Retry. A parts run that already wrote completion docs uses re-generate.
 * A pending URL-improve that never enqueued uses OpenAIArticleGenerate (parts).
 */
/** Retry one failed block. Other proposals stay until the new draft is merged. */
export async function retryFailedBlock(article: Article | null | undefined, proposalId: string): Promise<void> {
    if (!article?.id || !proposalId) {
        return;
    }
    await markPartsRunStarted(article);
    const request = clientEnhanceRequest(article);
    await Functions.OpenAIArticleReGenerate({...request, retryProposalId: proposalId});
    await adoptRunInProgress(article);
}

/**
 * A refused re-generate (another tab's run is busy, or a newer run holds the
 * article) still answers success. The tab then follows the article's clock
 * and status, not the start time markPartsRunStarted set locally, or
 * watchForAi drops every snapshot of the running run as older. After a
 * successful claim the article carries the server's start of this retry.
 */
export async function beginPartsRetry(article: Article | null | undefined): Promise<void> {
    if (!article?.id) {
        return;
    }
    const parts = await loadPartSnapshots(article.id);
    await markPartsRunStarted(article);
    const request = clientEnhanceRequest(article);
    if (parts.length === 0) {
        await Functions.OpenAIArticleGenerate(request).catch((error) => {
            if (!generationAlreadyRunning(error)) {
                throw error;
            }
        });
    } else {
        await Functions.OpenAIArticleReGenerate(request);
    }
    await adoptRunInProgress(article);
}
